import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import * as api from '../lib/api';
import { handleNotificationCreated } from '../hooks/useNotifications';
import { handleProjectStatusChanged } from '../hooks/useProject';
import { EventType } from '../types';
import { handleWorkflowRunUpdated } from '../hooks/useWorkflowRun';
import { handleCommentCreated, handleCommentUpdated, handleCommentDeleted } from '../hooks/useValidationComments';
import { invalidateAuditLogsQuery as handleAuditLogCreated } from '../hooks/useAuditLogs';

import { useAuth } from './AuthProvider';

export function EventStreamProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    return api.streamEvents(
      (event) => {
        switch (event.type) {
          case EventType.NOTIFICATION_CREATED: {
            handleNotificationCreated(queryClient, event.data);
            break;
          }
          case EventType.WORKFLOW_RUN_UPDATED: {
            handleWorkflowRunUpdated(queryClient, event.data);
            break;
          }
          case EventType.COMMENT_CREATED: {
            handleCommentCreated(queryClient, event.data.projectId, event.data);
            break;
          }
          case EventType.COMMENT_UPDATED: {
            handleCommentUpdated(queryClient, event.data.projectId, event.data);
            break;
          }
          case EventType.COMMENT_DELETED: {
            handleCommentDeleted(queryClient, event.data.projectId, event.data);
            break;
          }
          case EventType.PROJECT_STATUS_CHANGED: {
            handleProjectStatusChanged(queryClient, event.data.projectId, event.data.status);
            break;
          }
          case EventType.AUDIT_LOG_ENTRY_CREATED: {
            const { projectId } = event.data;

            if (projectId) {
              handleAuditLogCreated(queryClient, projectId);
            }
            break;
          }
          default:
            break;
        }
      },
      (error) => {
        console.error('Error subscribing to event stream:', error);
      }
    );
  }, [isAuthenticated, queryClient]);

  return <>{children}</>;
}
